import { Auth, Hub } from 'aws-amplify';
import { useFlags, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export interface IFeatureFlags {
  ProductUsageComparison: boolean;
  IsGrowerFixPrice: boolean;
  IsExchangeRate: boolean;
  ToShowEstimatedDetailsCards: boolean;
  IsGrowerRegistrationMobileValidation: boolean;
  ProductCommitmentsUsageLimit: boolean;
  IsRetailerNameEmailOptional: boolean;
  IsPayoutNotification: boolean;
  AdminResetPassword: boolean;
  IsRetailerDetailsVisible: boolean;
  IsTimeZoneEdit: boolean;
  showPrivacyStatement: boolean;
  EnableLockInPrice: boolean;
  sendAmplitudeEvents: boolean;
  enableSfdcField: boolean;
  isPayeeNameVisible: boolean;
  isPayeeNameEditable: boolean;
  UseNewCognitoPool: boolean;
  isRetailerViewAccess: boolean;
  isAllowBonusAgreement: boolean;
  IsDeleteEnrollmentEnabled: boolean;
  IsRevisedActualsVisible: boolean;
  superAdminDeleteCampaignUsers: boolean;
}
interface LDUser {
  key: string;
  name?: string;
  email?: string;
  custom?: Record<string, any>;
}
interface LDContextType {
  setLdUser: (user: LDUser) => void;
  flags: IFeatureFlags;
  ready: boolean;
}

const LDContext = createContext<LDContextType | undefined>(undefined);

export const useLD = (): LDContextType => {
  const ctx = useContext(LDContext);
  if (!ctx) throw new Error('useLD must be used within LDProvider');
  return ctx;
};

let ldClientInstance: ReturnType<typeof useLDClient> | null = null;
export const getLDClientInstance = () => ldClientInstance;

const InnerLDProviderComponent: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const ldClient = useLDClient();
  const rawFlags = useFlags<Record<string, boolean>>();
  const [ready, setReady] = useState(false);

  const resolveLdUserFromCognito = async (): Promise<LDUser | null> => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      return {
        key: user.attributes?.sub || user.attributes?.email || user.username,
        email: user.attributes?.email,
        name: user.attributes?.name,
        custom: {
          country:
            user?.attributes?.['custom:country_code'] || user?.attributes?.['custom:country'],
          role: user?.attributes?.['custom:role'],
          countryCode:
            user?.attributes?.['custom:country_code'] || user?.attributes?.['custom:countryCode'],
        },
      };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (ldClient) {
      ldClientInstance = ldClient;
    }
  }, [ldClient]);

  useEffect(() => {
    if (!ldClient) return;
    const onReady = () => {
      setReady(true);
    };
    ldClient.on('ready', onReady);
    return () => {
      ldClient.off('ready', onReady);
    };
  }, [ldClient]);

  // Re-identify the LD user on page refresh when already authenticated.
  // Hub auth events (signIn/cognitoHostedUI) only fire on explicit login,
  // not on refresh — so without this, flags stay evaluated for 'anonymous'.
  useEffect(() => {
    if (!ldClient) return;
    let cancelled = false;

    ldClient
      .waitForInitialization()
      .then(() => resolveLdUserFromCognito())
      .then((ldUser) => {
        if (!cancelled && ldUser?.key) {
          ldClient
            .identify(ldUser)
            .catch((e) => console.error('[LDContext] re-identify on refresh failed', e));
        }
      })
      .catch(() => {
        // User not authenticated — stay anonymous
      });

    return () => {
      cancelled = true;
    };
  }, [ldClient]);

  const setLdUser = (user: LDUser) => {
    if (!ldClient) {
      return;
    }
    ldClient.identify(user).catch((e) => console.error(e));
  };

  const mappedFlags: IFeatureFlags = useMemo(() => {
    const result = {} as IFeatureFlags;
    (Object.keys(flagKeyMap) as Array<keyof IFeatureFlags>).forEach((pascalKey) => {
      const camelKey = flagKeyMap[pascalKey];
      (result as any)[pascalKey] =
        (rawFlags as Record<string, boolean> | undefined)?.[camelKey] ?? false;
    });
    return result;
  }, [rawFlags]);

  const cleanCustomAttrs = (custom: Record<string, any> = {}) => {
    const result: Record<string, string> = {};
    Object.entries(custom || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        result[key] = String(value);
      }
    });
    return result;
  };

  useEffect(() => {
    const listener = (data: any) => {
      const event = data?.payload?.event;

      if (!ldClient) {
        console.warn('ldClient not ready yet, skipping event:', event);
        return;
      }

      if (event === 'signIn' || event === 'cognitoHostedUI') {
        ldClient
          .waitForInitialization()
          .then(async () => {
            const cognitoUser = await Auth.currentAuthenticatedUser();
            const ldUser = {
              key: String(cognitoUser.attributes?.sub || cognitoUser.username || '').trim(),
              email: cognitoUser.attributes?.email || undefined,
              name: cognitoUser.attributes?.name || undefined,
              custom: cleanCustomAttrs({
                country:
                  cognitoUser.attributes?.['custom:country_code'] ||
                  cognitoUser.attributes?.['custom:country'],
                role: cognitoUser.attributes?.['custom:role'],
                countryCode:
                  cognitoUser.attributes?.['custom:country_code'] ||
                  cognitoUser.attributes?.['custom:countryCode'],
              }),
            };

            if (!ldUser.key) {
              console.error('Invalid LD user: missing key after resolving from Cognito');
              return;
            }
            return ldClient.identify(ldUser);
          })
          .catch((e) => console.error('[LDContext] identify failed after init', e));
      }

      if (event === 'signOut') {
        ldClient.identify({ key: 'anonymous' }).catch(() => undefined);
      }
    };

    Hub.listen('auth', listener);
    return () => {
      Hub.remove('auth', listener);
    };
  }, [ldClient]);

  return (
    <LDContext.Provider value={{ setLdUser, flags: mappedFlags, ready }}>
      {children}
    </LDContext.Provider>
  );
};

const ldClientSideId = process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID;
if (!ldClientSideId) {
  throw new Error('REACT_APP_LAUNCH_DARKLY_CLIENT_ID environment variable is not set.');
}

const LDProviderWrapped = withLDProvider({
  clientSideID: ldClientSideId,
  user: { key: 'anonymous' },
  options: {
    bootstrap: 'localStorage',
    streaming: true,
  },
})(InnerLDProviderComponent);

export const LDProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <LDProviderWrapped>{children}</LDProviderWrapped>
);

export const flagKeyMap: Record<keyof IFeatureFlags, string> = {
  ProductUsageComparison: 'productUsageComparison',
  IsGrowerFixPrice: 'isGrowerFixPrice',
  IsExchangeRate: 'isExchangeRate',
  ToShowEstimatedDetailsCards: 'toShowEstimatedDetailsCards',
  IsGrowerRegistrationMobileValidation: 'isGrowerRegistrationMobileValidation',
  ProductCommitmentsUsageLimit: 'productCommitmentsUsageLimit',
  IsRetailerNameEmailOptional: 'isRetailerNameEmailOptional',
  IsPayoutNotification: 'isPayoutNotification',
  AdminResetPassword: 'adminResetPassword',
  IsRetailerDetailsVisible: 'isRetailerDetailsVisible',
  IsTimeZoneEdit: 'isTimeZoneEdit',
  showPrivacyStatement: 'showPrivacyStatement',
  EnableLockInPrice: 'enableLockInPrice',
  sendAmplitudeEvents: 'sendAmplitudeEvents',
  enableSfdcField: 'enableSfdcField',
  isPayeeNameVisible: 'isPayeeNameVisible',
  isPayeeNameEditable: 'isPayeeNameEditable',
  UseNewCognitoPool: 'useNewCognitoPool',
  isRetailerViewAccess: 'isRetailerViewAccess',
  isAllowBonusAgreement: 'isAllowBonusAgreement',
  IsDeleteEnrollmentEnabled: 'isDeleteEnrollmentEnabled',
  IsRevisedActualsVisible: 'isRevisedActualsVisible',
  superAdminDeleteCampaignUsers: 'superAdminDeleteCampaignUsers',
};
